import { createContext, useContext, useState, useEffect } from 'react';
import i18n from 'i18next';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

const SUPPORTED = new Set(['fr', 'en']);

/**
 * Apply the backend-stored language preference to i18next + localStorage.
 *
 * Backend is authoritative. When a user is authenticated, their account preference
 * always wins over the LanguageDetector's navigator guess — which would otherwise
 * cache `en-US` into localStorage on first visit and silently lock the user into
 * the wrong language.
 *
 * The LanguageSwitcher always writes both to localStorage and to the backend in
 * a single round-trip, so the two stay in sync.
 */
const applyUserLanguage = (user) => {
    const lng = (user?.preferred_language || '').slice(0, 2);
    if (!SUPPORTED.has(lng)) return;
    if (i18n.resolvedLanguage !== lng) {
        i18n.changeLanguage(lng);
    }
    try { localStorage.setItem('uc.lang', lng); } catch { /* private mode */ }
    document.documentElement.lang = lng;
};

/**
 * Admin auth context — Wave-2 cookie edition.
 *
 * The JWT lives exclusively in the `uc_admin_token` httpOnly cookie
 * managed by the backend. JavaScript can no longer read or write it,
 * closing the XSS token-theft vector called out in the security audit.
 *
 * `user` state is populated by a `/auth/me` fetch on mount — the
 * request will succeed IFF the browser is still holding a valid
 * session cookie, so this doubles as our "am I signed in" check.
 */
export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Cookie is opaque to JS — we discover the session state by
        // hitting /auth/me and letting the server tell us who we are.
        authAPI.getMe()
            .then(res => {
                setUser(res.data);
                applyUserLanguage(res.data);
            })
            .catch(() => {
                setUser(null);
            })
            .finally(() => setLoading(false));
    }, []);

    const login = async (username, password) => {
        const response = await authAPI.login({ username, password });
        // Server has already set the httpOnly cookie on this response.
        // We just cache the user profile for immediate UI hydration.
        const { user: userData } = response.data;
        setUser(userData);
        applyUserLanguage(userData);
        return userData;
    };

    const logout = async () => {
        try { await authAPI.logout(); } catch { /* best-effort */ }
        setUser(null);
    };

    const isAdmin = () => user?.role === 'admin';

    return (
        <AuthContext.Provider value={{ user, login, logout, loading, isAdmin }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};
